import { calledBy } from "../../me/called.ts";
export type Step = { tool: string; ok: boolean; object?: string };

export type Frame = { did: readonly Step[]; more: number };

export type Someone = {
  account: string;
  handle: string;
  displayName: string | null;

  run?: string | null;

  startedAt?: string | null;

  frame?: Frame | null;
};

export type Cap = { scope: string; id: string; period: string; amount: number; spent: number };

export function nameOf(who: Someone): string {
  return calledBy(who) ?? who.handle;
}

export function whatIsWorking(
  busy: { thinking: Someone[]; unanswered: Someone[] },
  words: { one: (who: string) => string; many: (howMany: number) => string; owed: (who: string) => string },
): string | null {
  const thinking = busy.thinking;
  if (thinking.length === 1 && thinking[0]) return words.one(nameOf(thinking[0]));
  if (thinking.length > 1) return words.many(thinking.length);
  const owed = busy.unanswered[0];
  if (owed) return words.owed(nameOf(owed));
  return null;
}

export function whatIsRunning(busy: { thinking: Someone[] }): string[] {
  const runs: string[] = [];
  for (const one of busy.thinking) if (one.run) runs.push(one.run);
  return runs;
}

export type State = "thinking" | "waiting" | "idle";

export function stateOf(account: string, busy: { thinking: Someone[]; unanswered: Someone[] } | null): State {
  if (!busy) return "idle";
  if (busy.thinking.some((one) => one.account === account)) return "thinking";
  if (busy.unanswered.some((one) => one.account === account)) return "waiting";
  return "idle";
}

export function turnOf(account: string, busy: { thinking: Someone[] } | null): Someone | null {
  return busy?.thinking.find((one) => one.account === account) ?? null;
}

const TOO_LONG = 90 * 60 * 1000;

export function elapsedSaid(startedAt: string | null | undefined, now: number): string | null {
  if (!startedAt) return null;
  const began = new Date(startedAt).getTime();
  if (!Number.isFinite(began)) return null;
  const span = now - began;
  if (span < 0 || span > TOO_LONG) return null;
  const seconds = Math.floor(span / 1000);
  const twice = (figure: number) => String(figure).padStart(2, "0");
  if (seconds < 3600) return `${Math.floor(seconds / 60)}:${twice(seconds % 60)}`;
  return `${Math.floor(seconds / 3600)}:${twice(Math.floor(seconds / 60) % 60)}:${twice(seconds % 60)}`;
}

export function frameOf(frame: Frame | null | undefined, most: number): Frame | null {
  if (!frame) return null;
  const did = frame.did.slice(Math.max(0, frame.did.length - most));
  const more = Math.max(0, frame.more) + (frame.did.length - did.length);
  if (did.length === 0 && more === 0) return null;
  return { did, more };
}

export function toolsIn(frame: Frame | null | undefined): number {
  return frame ? frame.did.length + Math.max(0, frame.more) : 0;
}

export function whatIsLeft(caps: readonly Cap[]): number | null {
  let worst: number | null = null;
  for (const cap of caps) {
    if (cap.amount <= 0) continue;
    const left = Math.max(0, Math.min(1, (cap.amount - cap.spent) / cap.amount));
    worst = worst === null ? left : Math.min(worst, left);
  }
  return worst;
}
